import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  ListItemText,
} from '@mui/material';
import AttritionReport from './AttritionReport';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_REPORT_YEAR = CURRENT_YEAR - 1;
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const COMPANIES_TIMEOUT_MS = 25000;

const selectSx = {
  minHeight: 56,
  fontSize: 16,
  bgcolor: '#fff',
  '& .MuiSelect-select': {
    py: 1.75,
    px: 2,
    display: 'flex',
    alignItems: 'center',
  },
};

/**
 * Year + company picker for admin/SPOC attrition views.
 * Company dropdown only lists companies with usable stipend CSV data for the selected year.
 */
function AttritionReportPicker({
  companiesApiPath,
  reportApiPath,
  tokenKey,
  title = 'Employee Attrition Report',
  subtitle = 'Select year and company. Only companies with stipend CSV data appear in the list.',
  redirectOnAuthFailure = false,
}) {
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), COMPANIES_TIMEOUT_MS)
      : null;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setCompaniesError('');
      setCompanies([]);
      setSelectedCompanyId('');

      try {
        const token = localStorage.getItem(tokenKey);
        if (!token) {
          setCompaniesError('Authentication required. Please log in again.');
          return;
        }

        const response = await fetch(
          `${API_URL}${companiesApiPath}?year=${year}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            ...(controller ? { signal: controller.signal } : {}),
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to load companies');
        }

        if (cancelled) return;
        const list = Array.isArray(data.companies) ? data.companies : [];
        setCompanies(list);
        if (list.length === 1) {
          setSelectedCompanyId(String(list[0].id));
        }
      } catch (err) {
        if (cancelled) return;
        const aborted = err.name === 'AbortError';
        setCompaniesError(
          aborted
            ? 'Company list timed out. Try again, or pick a year with stipend CSV uploads (e.g. 2025).'
            : (err.message || 'Failed to load companies')
        );
        setCompanies([]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (controller) controller.abort();
    };
  }, [year, companiesApiPath, tokenKey]);

  const selectedCompany = companies.find((c) => String(c.id) === String(selectedCompanyId));

  const companyLabel = (company) => {
    if (!company) return '';
    const code = company.establishment_code ? ` (${company.establishment_code})` : '';
    return `${company.company_name || 'Company'}${code}`;
  };

  return (
    <Box sx={{ py: 2, width: '100%', maxWidth: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight={700} color="#0f766e">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        <FormControl
          sx={{
            width: { xs: '100%', sm: 160 },
            minWidth: { xs: '100%', sm: 160 },
            flexShrink: 0,
          }}
        >
          <InputLabel id="attrition-year-label" sx={{ fontSize: 16 }}>Year</InputLabel>
          <Select
            labelId="attrition-year-label"
            value={year}
            label="Year"
            size="medium"
            onChange={(e) => setYear(Number(e.target.value))}
            sx={selectSx}
          >
            {YEARS.map((y) => (
              <MenuItem key={y} value={y} sx={{ fontSize: 16, py: 1.25 }}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          disabled={loadingCompanies || companies.length === 0}
          sx={{
            flex: '1 1 420px',
            minWidth: { xs: '100%', sm: 320 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <InputLabel id="attrition-company-label" sx={{ fontSize: 16 }}>Company</InputLabel>
          <Select
            labelId="attrition-company-label"
            value={selectedCompanyId}
            label="Company"
            size="medium"
            displayEmpty={false}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            sx={selectSx}
            renderValue={(value) => {
              const company = companies.find((c) => String(c.id) === String(value));
              return companyLabel(company) || 'Select company';
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 420,
                  minWidth: 360,
                  '& .MuiMenuItem-root': { whiteSpace: 'normal' },
                },
              },
            }}
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={String(company.id)} sx={{ py: 1.25, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={companyLabel(company)}
                  secondary={`${company.totalEmployees} employees · ${company.monthsWithStipendData} months with data${
                    company.averageAttritionRate != null ? ` · avg attrition ${company.averageAttritionRate}%` : ''
                  }`}
                  primaryTypographyProps={{ fontSize: 15, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 13 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loadingCompanies ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading companies with stipend CSV for {year}…
          </Typography>
        </Box>
      ) : companiesError ? (
        <Alert severity="error" sx={{ mb: 3 }}>{companiesError}</Alert>
      ) : companies.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No companies with attrition data for {year}. Try another year or ensure stipend CSV uploads exist.
        </Alert>
      ) : !selectedCompanyId ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Select a company to view the attrition report for {year}.
        </Alert>
      ) : (
        <AttritionReport
          key={`${reportApiPath}-${selectedCompanyId}-${year}`}
          companyName={selectedCompany?.company_name}
          companyId={selectedCompanyId}
          apiPath={reportApiPath}
          tokenKey={tokenKey}
          redirectOnAuthFailure={redirectOnAuthFailure}
          year={year}
          hideYearSelector
          hideHeader
        />
      )}
    </Box>
  );
}

export default AttritionReportPicker;
